import { PapiUser } from '@wix/da-papi-types';
import React, { useRef, useEffect } from 'react';
import { ListItemButton } from '@wix/da-ds/pkg/List/ListItem';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import StreamedList from '../../../StreamedList';
import { ResourceType } from '../../../types/resource';
import UserAvatar from '../../../User/Avatar';
import BasicUserName from '../../../User/BasicUserName';
import {
  BiContextProvidedValue,
  BiData,
  UserOrGroupFeedSelectBiEvent,
} from '@wix/da-bi/pkg/events';

export interface Props {
  id?: string;
  itemIdGenerator?: (id: number) => string;
  itemBiData?: any;
  streamId: string;
  isLoading: boolean;
  users: PapiUser[];
  selectedUserId?: number;
  keyboardSelectedIndex?: number;
  className?: string;
  onItemClick?: (user: PapiUser) => void;
  itemSuffix?: React.ReactNode;
  showSuffixOnlyOnHover?: boolean;
  emptyView?: React.ReactNode;
}

const UserList: React.FC<Props> = ({
  id,
  itemIdGenerator,
  itemBiData,
  streamId,
  users,
  selectedUserId,
  keyboardSelectedIndex = -1,
  isLoading,
  className,
  onItemClick,
  itemSuffix,
  showSuffixOnlyOnHover,
  emptyView = null,
}) => {
  const scrollableRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // here we handle scrolling to the selected element when navigating by keyboard
    const scrollable = scrollableRef.current;
    const selectedNode: HTMLElement | null | undefined =
      scrollable?.querySelector<HTMLElement>(`[data-selected]`);
    if (!selectedNode || !scrollable || keyboardSelectedIndex < 0) {
      return;
    }

    if (
      selectedNode.offsetTop + selectedNode.offsetHeight <
        scrollable.scrollTop + scrollable.offsetHeight &&
      selectedNode.offsetTop > scrollable.scrollTop
    ) {
      return;
    }

    // position to bottom
    scrollable.scrollTop =
      selectedNode.offsetTop + selectedNode.offsetHeight - scrollable.offsetHeight;
  }, [scrollableRef, keyboardSelectedIndex]);

  return (
    <StreamedList
      id={id}
      role="listbox"
      streamId={streamId}
      className={className}
      innerRef={node => (scrollableRef.current = node)}
    >
      {!isLoading && users.length === 0 && emptyView}
      {users.map((user, index) => {
        const isSelectedViaKeyboard = keyboardSelectedIndex === index;

        return (
          <div
            id={itemIdGenerator ? itemIdGenerator(index) : undefined}
            role="option"
            aria-selected={isSelectedViaKeyboard}
            key={user.userId}
            aria-label={user.username}
            data-selected={isSelectedViaKeyboard ? 1 : undefined}
          >
            <ListItemButton
              prefix={
                <UserAvatar
                  withTooltip={false}
                  withLink={false}
                  user={user}
                  size={40}
                  aria-hidden="true"
                />
              }
              suffix={itemSuffix}
              showSuffixOnlyOnHover={showSuffixOnlyOnHover}
              label={<BasicUserName user={user} />}
              checked={selectedUserId === user.userId || isSelectedViaKeyboard}
              onClick={onItemClick && (() => onItemClick(user))}
              biData={
                typeof itemBiData !== 'undefined'
                  ? itemBiData
                  : BiData<UserOrGroupFeedSelectBiEvent>({
                      evid: 402,
                      typeid: ResourceType.USER,
                      itemid: user.userId,
                      sectionname: BiContextProvidedValue,
                    })
              }
              fillContainerWidth
            />
          </div>
        );
      })}
      {isLoading && <LoadingIndicator />}
    </StreamedList>
  );
};
UserList.displayName = 'UserList';

export default UserList;
